import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import PortalLayout from '../../components/Portal/PortalLayout';
import { walletAPI, memberAPI } from '../../services/api';

/**
 * PortalWallet - Member's wallet balance and transaction history
 */
const PortalWallet = () => {
  const navigate = useNavigate();
  const [wallet, setWallet] = useState(null);
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    const fetchWalletData = async () => {
      try {
        setLoading(true);
        const user = JSON.parse(localStorage.getItem('user') || '{}');

        if (!user.memberId) {
          setError('Member information not found');
          return;
        }

        // Get member's wallet
        const walletRes = await memberAPI.getMemberWallet(user.memberId);
        const walletData = walletRes.data?.data || walletRes.data;

        if (walletData) {
          setWallet(walletData);

          // Get wallet transactions
          const txRes = await walletAPI.getTransactionHistory(walletData._id, { limit: 50 });
          setTransactions(txRes.data?.data?.transactions || txRes.data?.transactions || []);
        } else {
          setError('No wallet found. Contact tournament director to set up your wallet.');
        }
      } catch (err) {
        console.error('Failed to load wallet:', err);
        setError('Failed to load wallet information');
      } finally {
        setLoading(false);
      }
    };

    fetchWalletData();
  }, []);

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit'
    });
  };

  const getTransactionIcon = (type) => {
    switch (type) {
      case 'deposit':
        return (
          <svg className="h-5 w-5 text-green-600" fill="currentColor" viewBox="0 0 20 20">
            <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-11a1 1 0 10-2 0v3.586L7.707 9.293a1 1 0 00-1.414 1.414l3 3a1 1 0 001.414 0l3-3a1 1 0 00-1.414-1.414L11 10.586V7z" clipRule="evenodd" />
          </svg>
        );
      case 'withdrawal':
        return (
          <svg className="h-5 w-5 text-red-600" fill="currentColor" viewBox="0 0 20 20">
            <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-11a1 1 0 10-2 0v3.586L7.707 9.293a1 1 0 00-1.414 1.414l3 3a1 1 0 001.414 0l3-3a1 1 0 00-1.414-1.414L11 10.586V7z" clipRule="evenodd" />
          </svg>
        );
      case 'transfer':
        return (
          <svg className="h-5 w-5 text-blue-600" fill="currentColor" viewBox="0 0 20 20">
            <path d="M8 5a1 1 0 100 2h5.586l-1.293 1.293a1 1 0 001.414 1.414l3-3a1 1 0 000-1.414l-3-3a1 1 0 10-1.414 1.414L13.586 5H8zM12 15a1 1 0 100-2H6.414l1.293-1.293a1 1 0 10-1.414-1.414l-3 3a1 1 0 000 1.414l3 3a1 1 0 001.414-1.414L6.414 15H12z" />
          </svg>
        );
      default:
        return (
          <svg className="h-5 w-5 text-gray-600" fill="currentColor" viewBox="0 0 20 20">
            <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-12a1 1 0 10-2 0v4a1 1 0 00.293.707l2.828 2.829a1 1 0 101.415-1.415L11 9.586V6z" clipRule="evenodd" />
          </svg>
        );
    }
  };

  if (loading) {
    return (
      <PortalLayout title="Wallet">
        <div className="flex justify-center py-16">
          <div className="animate-spin h-8 w-8 border-4 border-green-500 border-t-transparent rounded-full" />
        </div>
      </PortalLayout>
    );
  }

  if (error) {
    return (
      <PortalLayout title="Wallet">
        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-4">
          <p className="text-yellow-800">{error}</p>
        </div>
        <button
          onClick={() => navigate('/portal/tournaments')}
          className="text-green-600 text-sm font-medium"
        >
          ← Back to Tournaments
        </button>
      </PortalLayout>
    );
  }

  return (
    <PortalLayout title="Wallet">
      <div className="space-y-6">
        {/* Balance Card */}
        <div className="bg-gradient-to-br from-green-600 to-green-700 rounded-2xl p-6 text-white shadow-lg">
          <p className="text-green-100 text-sm uppercase tracking-wide mb-2">
            Available Balance
          </p>
          <p className="text-4xl font-bold mb-1">
            ${wallet?.balance?.toFixed(2) || '0.00'}
          </p>
          <p className="text-green-200 text-xs">
            {wallet?.ownerType === 'member' ? 'Personal Wallet' : wallet?.ownerType}
          </p>
        </div>

        {/* Transaction History */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
          <div className="px-4 py-3 border-b border-gray-100">
            <h3 className="font-semibold text-gray-900">Transaction History</h3>
            <p className="text-xs text-gray-500 mt-1">
              {transactions.length} transaction{transactions.length !== 1 ? 's' : ''}
            </p>
          </div>

          {transactions.length > 0 ? (
            <div className="divide-y divide-gray-100">
              {transactions.slice(0, 20).map((tx) => (
                <div key={tx._id} className="px-4 py-3 hover:bg-gray-50 transition-colors">
                  <div className="flex items-start gap-3">
                    <div className="flex-shrink-0 mt-0.5">
                      {getTransactionIcon(tx.type)}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-gray-900 truncate">
                        {tx.description || tx.type}
                      </p>
                      <p className="text-xs text-gray-500 mt-0.5">
                        {formatDate(tx.createdAt)}
                      </p>
                      {tx.relatedEntity && (
                        <p className="text-xs text-gray-400 mt-0.5 truncate">
                          {tx.relatedEntity}
                        </p>
                      )}
                    </div>
                    <div className="flex-shrink-0">
                      <p className={`text-sm font-semibold ${
                        tx.type === 'deposit' ? 'text-green-600' :
                        tx.type === 'withdrawal' ? 'text-red-600' :
                        'text-gray-900'
                      }`}>
                        {tx.type === 'deposit' ? '+' : '-'}${Math.abs(tx.amount).toFixed(2)}
                      </p>
                      {tx.status && tx.status !== 'completed' && (
                        <span className={`inline-block mt-1 text-xs px-2 py-0.5 rounded-full ${
                          tx.status === 'pending' ? 'bg-yellow-100 text-yellow-700' :
                          tx.status === 'failed' ? 'bg-red-100 text-red-700' :
                          'bg-gray-100 text-gray-700'
                        }`}>
                          {tx.status}
                        </span>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="px-4 py-12 text-center">
              <div className="text-4xl mb-2">💳</div>
              <p className="text-gray-500 text-sm">No transactions yet</p>
            </div>
          )}
        </div>

        {/* Info Section */}
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
          <div className="flex items-start gap-3">
            <svg className="h-5 w-5 text-blue-600 flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
            </svg>
            <div className="text-sm text-blue-800">
              <p className="font-medium mb-1">About Your Wallet</p>
              <p className="text-blue-700">
                Your wallet is used for side action contests and other tournament activities.
                Funds are managed by the tournament director. Contact them for deposits or questions.
              </p>
            </div>
          </div>
        </div>
      </div>
    </PortalLayout>
  );
};

export default PortalWallet;
